<script setup>
// NOTICE: KAPTION-INTEGRATION
// team-location.SETTINGS.2 — Settings › Ubicación del equipo (administrators).
// Mode (Desactivado / Opcional / Obligatorio) + options, a confirmation before
// switching to Obligatorio, a live preview of the notice the phone shows, and
// the signing secret of the `agent_location_updated` webhook. While the
// `team_location_tracking` permission is off the page only explains that.
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import KaptionTeamLocationAPI from 'dashboard/api/kaptionTeamLocation';
import {
  MODES,
  PRECISIONS,
  WHENS,
  INTERVALS,
  RETENTIONS,
  audienceSummary,
  isDirty,
  phonePreview,
} from './kaptionTeamLocationSettings';

const { t, locale } = useI18n();
const store = useStore();

const isLoading = ref(true);
const isSaving = ref(false);
const payload = ref(null);
const form = ref(null);
const explainer = ref(null);
const showSecret = ref(false);
// team-location.EXPLAINER.1 — the preview follows the phone: iPhone shows the
// server notice as is; Android adds Google Play's prominent disclosure.
const previewPlatform = ref('ios');
const { currentAccount } = useAccount();
const confirmDialog = ref(null);

const enabled = computed(() => payload.value?.enabled);
const saved = computed(() => payload.value?.settings || {});
const dirty = computed(() => isDirty(form.value, saved.value));
const teams = computed(() => store.getters['teams/getTeams'] || []);
const agents = computed(() => store.getters['agents/getAgents'] || []);
const audience = computed(() =>
  audienceSummary(payload.value?.audience, {
    t,
    teams: teams.value,
    agents: agents.value,
  })
);
const preview = computed(() =>
  phonePreview({
    explainer: explainer.value,
    platform: previewPlatform.value,
    mode: form.value?.mode,
    when: form.value?.when,
    accountName: currentAccount.value?.name || '',
    t,
  })
);
const maskedSecret = computed(() => {
  const secret = payload.value?.webhook?.signing_secret || '';
  if (showSecret.value) return secret;
  return secret ? `${secret.slice(0, 14)}••••••••••••••••` : '';
});

const localeParam = () => locale.value;

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await KaptionTeamLocationAPI.getSettings({
      locale: localeParam(),
    });
    payload.value = data;
    form.value = { ...data.settings };
    explainer.value = data.explainer;
  } catch (error) {
    useAlert(t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const refreshPreview = debounce(
  async () => {
    if (!form.value) return;
    try {
      const { data } = await KaptionTeamLocationAPI.previewExplainer({
        ...form.value,
        locale: localeParam(),
      });
      explainer.value = data.explainer;
    } catch (error) {
      // The preview is best-effort; the saved explainer stays visible.
    }
  },
  300,
  false
);

watch(form, () => refreshPreview(), { deep: true });

const pickMode = mode => {
  if (mode === 'mandatory' && saved.value.mode !== 'mandatory') {
    confirmDialog.value?.open();
    return;
  }
  form.value.mode = mode;
};

const confirmMandatory = () => {
  form.value.mode = 'mandatory';
  confirmDialog.value?.close();
};

const save = async () => {
  isSaving.value = true;
  try {
    const { data } = await KaptionTeamLocationAPI.updateSettings({
      ...form.value,
      locale: localeParam(),
    });
    payload.value = data;
    form.value = { ...data.settings };
    useAlert(t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.SAVED'));
  } catch (error) {
    useAlert(t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const copySecret = async () => {
  await copyTextToClipboard(payload.value?.webhook?.signing_secret || '');
  useAlert(t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.WEBHOOK.COPIED'));
};

const rotateSecret = async () => {
  try {
    const { data } = await KaptionTeamLocationAPI.rotateWebhookSecret();
    payload.value = { ...payload.value, webhook: data.webhook };
    showSecret.value = true;
    useAlert(t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.WEBHOOK.ROTATED'));
  } catch (error) {
    useAlert(t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.SAVE_ERROR'));
  }
};

onMounted(() => {
  load();
  store.dispatch('teams/get');
  store.dispatch('agents/get');
});

const optionLabel = (group, value) =>
  t(
    `KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.${group}.${String(value).toUpperCase()}`
  );
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.HEADER')"
        :description="t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.DESCRIPTION')"
      >
        <template v-if="enabled" #actions>
          <router-link
            v-if="payload?.view_enabled"
            :to="{ name: 'kaption_team_location_map' }"
          >
            <Button
              icon="i-lucide-map"
              variant="faded"
              color="slate"
              :label="t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.OPEN_MAP')"
            />
          </router-link>
          <Button
            icon="i-lucide-save"
            :label="t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.SAVE')"
            :is-loading="isSaving"
            :disabled="!dirty"
            data-testid="ktl-save"
            @click="save"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div
        v-if="!enabled"
        class="flex flex-col items-start gap-3 p-6 border rounded-xl border-n-weak bg-n-solid-2"
        data-testid="ktl-not-enabled"
      >
        <span class="text-2xl i-lucide-map-pin-off text-n-slate-10" />
        <h2 class="text-base font-semibold text-n-slate-12">
          {{ t('KAPTION_PLATFORM.TEAM_LOCATION.NOT_ENABLED') }}
        </h2>
        <p class="max-w-xl text-sm text-n-slate-11">
          {{ t('KAPTION_PLATFORM.TEAM_LOCATION.NOT_ENABLED_HINT') }}
        </p>
        <router-link :to="{ name: 'settings_feature_management' }">
          <Button
            variant="faded"
            color="slate"
            icon="i-lucide-sliders-horizontal"
            :label="t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.GO_TO_FEATURES')"
          />
        </router-link>
      </div>

      <div
        v-else-if="form"
        class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]"
      >
        <div class="flex flex-col gap-6">
          <section class="flex flex-col gap-3">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.MODE.LABEL') }}
            </h3>
            <div class="grid gap-3 sm:grid-cols-3">
              <button
                v-for="mode in MODES"
                :key="mode"
                type="button"
                class="flex flex-col gap-1 p-4 text-left border rounded-xl transition-colors"
                :class="
                  form.mode === mode
                    ? 'border-n-brand bg-n-brand/5 ring-1 ring-n-brand'
                    : 'border-n-weak bg-n-solid-2 hover:bg-n-alpha-2'
                "
                :data-testid="`ktl-mode-${mode}`"
                @click="pickMode(mode)"
              >
                <span class="text-sm font-semibold text-n-slate-12">
                  {{ optionLabel('MODE', mode) }}
                </span>
                <span class="text-xs text-n-slate-11">
                  {{
                    t(
                      `KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.MODE.${mode.toUpperCase()}_HINT`
                    )
                  }}
                </span>
              </button>
            </div>
          </section>

          <section
            class="grid gap-5 p-5 border rounded-xl border-n-weak bg-n-solid-2 sm:grid-cols-2"
            :class="form.mode === 'off' ? 'opacity-60' : ''"
          >
            <fieldset class="flex flex-col gap-2">
              <legend class="mb-2 text-sm font-semibold text-n-slate-12">
                {{
                  t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.PRECISION.LABEL')
                }}
              </legend>
              <label
                v-for="value in PRECISIONS"
                :key="value"
                class="flex items-center gap-2 text-sm text-n-slate-12"
              >
                <input
                  v-model="form.precision"
                  type="radio"
                  :value="value"
                  class="!m-0"
                />
                {{ optionLabel('PRECISION', value) }}
              </label>
            </fieldset>

            <fieldset class="flex flex-col gap-2">
              <legend class="mb-2 text-sm font-semibold text-n-slate-12">
                {{ t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.WHEN.LABEL') }}
              </legend>
              <label
                v-for="value in WHENS"
                :key="value"
                class="flex items-center gap-2 text-sm text-n-slate-12"
              >
                <input
                  v-model="form.when"
                  type="radio"
                  :value="value"
                  class="!m-0"
                />
                {{ optionLabel('WHEN', value) }}
              </label>
            </fieldset>

            <label
              class="flex flex-col gap-1 text-sm font-semibold text-n-slate-12"
            >
              {{ t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.INTERVAL.LABEL') }}
              <select
                v-model.number="form.interval_seconds"
                class="!mb-0 h-9 px-2 font-normal rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
              >
                <option v-for="value in INTERVALS" :key="value" :value="value">
                  {{ optionLabel('INTERVAL', value) }}
                </option>
              </select>
            </label>

            <label
              class="flex flex-col gap-1 text-sm font-semibold text-n-slate-12"
            >
              {{ t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.RETENTION.LABEL') }}
              <select
                v-model.number="form.retention_days"
                class="!mb-0 h-9 px-2 font-normal rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
              >
                <option v-for="value in RETENTIONS" :key="value" :value="value">
                  {{
                    t(
                      'KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.RETENTION.DAYS',
                      {
                        count: value,
                      }
                    )
                  }}
                </option>
              </select>
            </label>
          </section>

          <section
            class="flex flex-col gap-2 p-5 border rounded-xl border-n-weak bg-n-solid-2"
          >
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.AUDIENCE.LABEL') }}
            </h3>
            <p class="text-sm text-n-slate-12" data-testid="ktl-audience">
              {{ audience }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.AUDIENCE.HINT') }}
              <router-link
                :to="{ name: 'settings_feature_management' }"
                class="text-n-brand hover:underline"
              >
                {{
                  t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.GO_TO_FEATURES')
                }}
              </router-link>
            </p>
          </section>

          <section
            class="flex flex-col gap-3 p-5 border rounded-xl border-n-weak bg-n-solid-2"
          >
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.WEBHOOK.TITLE') }}
            </h3>
            <p class="text-xs text-n-slate-11">
              {{
                t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.WEBHOOK.BODY', {
                  event: payload.webhook.event,
                  header: payload.webhook.signature_header,
                })
              }}
              <router-link
                :to="{ name: 'settings_integrations_webhook' }"
                class="text-n-brand hover:underline"
              >
                {{
                  t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.WEBHOOK.MANAGE')
                }}
              </router-link>
            </p>
            <div class="flex flex-wrap items-center gap-2">
              <code
                class="px-2 py-1 text-xs rounded-md bg-n-alpha-2 text-n-slate-12 break-all"
                data-testid="ktl-secret"
              >
                {{ maskedSecret }}
              </code>
              <Button
                size="xs"
                variant="ghost"
                color="slate"
                :icon="showSecret ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                :label="
                  t(
                    showSecret
                      ? 'KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.WEBHOOK.HIDE'
                      : 'KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.WEBHOOK.REVEAL'
                  )
                "
                @click="showSecret = !showSecret"
              />
              <Button
                size="xs"
                variant="ghost"
                color="slate"
                icon="i-lucide-copy"
                :label="
                  t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.WEBHOOK.COPY')
                "
                @click="copySecret"
              />
              <Button
                size="xs"
                variant="ghost"
                color="ruby"
                icon="i-lucide-refresh-cw"
                :label="
                  t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.WEBHOOK.ROTATE')
                "
                @click="rotateSecret"
              />
            </div>
            <p class="text-xs text-n-slate-11">
              {{
                t(
                  'KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.WEBHOOK.SUBSCRIBED',
                  {
                    count: payload.webhook.subscribed_count,
                  }
                )
              }}
            </p>
          </section>
        </div>

        <aside class="flex flex-col gap-2">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.PREVIEW.TITLE') }}
          </h3>
          <div
            class="flex self-start gap-1 p-0.5 rounded-lg bg-n-alpha-2"
            role="radiogroup"
            :aria-label="
              t(
                'KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.PREVIEW.PLATFORMS_LABEL'
              )
            "
          >
            <button
              v-for="platform in ['ios', 'android']"
              :key="platform"
              type="button"
              role="radio"
              :aria-checked="previewPlatform === platform"
              :data-testid="`ktl-preview-${platform}`"
              class="px-2.5 py-1 text-xs font-medium rounded-md"
              :class="
                previewPlatform === platform
                  ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11'
              "
              @click="previewPlatform = platform"
            >
              {{
                t(
                  `KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.PREVIEW.${
                    platform === 'ios' ? 'PLATFORM_IOS' : 'PLATFORM_ANDROID'
                  }`
                )
              }}
            </button>
          </div>
          <div
            class="p-3 border shadow-sm rounded-[1.75rem] border-n-weak bg-n-alpha-1"
            data-testid="ktl-preview"
          >
            <div
              class="flex flex-col gap-3 p-4 rounded-[1.25rem] bg-n-solid-1 min-h-[22rem]"
            >
              <span
                class="flex items-center justify-center rounded-full size-10 bg-n-brand/10 text-n-brand"
              >
                <span class="text-xl i-lucide-map-pin" />
              </span>
              <h4 class="text-base font-semibold text-n-slate-12">
                {{ preview.title }}
              </h4>
              <p
                v-if="preview.disclosure"
                class="text-sm font-medium leading-relaxed text-n-slate-12"
                data-testid="ktl-preview-disclosure"
              >
                {{ preview.disclosure }}
              </p>
              <p class="text-sm leading-relaxed text-n-slate-11">
                {{ preview.body }}
              </p>
              <div class="flex flex-col gap-2 mt-auto">
                <span
                  class="py-2 text-sm font-medium text-center text-white rounded-lg bg-n-brand"
                >
                  {{ preview.primary }}
                </span>
                <span
                  v-if="preview.secondary"
                  class="py-2 text-sm text-center rounded-lg text-n-slate-11 bg-n-alpha-2"
                >
                  {{ preview.secondary }}
                </span>
              </div>
            </div>
          </div>
          <p class="text-xs text-n-slate-11">
            {{ t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.PREVIEW.HINT') }}
          </p>
        </aside>
      </div>

      <Dialog
        ref="confirmDialog"
        type="alert"
        :title="
          t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.CONFIRM_MANDATORY.TITLE')
        "
        :description="
          t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.CONFIRM_MANDATORY.BODY')
        "
        :confirm-button-label="
          t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.CONFIRM_MANDATORY.CONFIRM')
        "
        :cancel-button-label="
          t('KAPTION_PLATFORM.TEAM_LOCATION.SETTINGS.CONFIRM_MANDATORY.CANCEL')
        "
        @confirm="confirmMandatory"
      />
    </template>
  </SettingsLayout>
</template>
